import { useState, useEffect } from 'react';
import { Bell, BellSlash, X, Clock, CheckCircle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useNotifications } from '../context/NotificationContext';
import { MaintenanceSchedule } from '../lib/api';

interface Reminder {
  id: string;
  title: string;
  daysUntil: number;
  nextDate: Date;
  category?: string;
  type: 'maintenance';
}

interface ReminderWidgetProps {
  schedules?: MaintenanceSchedule[];
  onDismiss?: (id: string) => void;
  onSnooze?: (reminder: Reminder) => void;
}

export function ReminderWidget({ schedules = [], onDismiss, onSnooze }: ReminderWidgetProps) {
  const { permission, checkMaintenanceReminders, requestPermission, showNotification } = useNotifications();
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    const saved = localStorage.getItem('dismissed_reminders');
    return saved ? JSON.parse(saved) : [];
  });
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [expanded, setExpanded] = useState(false);

  // Check for reminders when schedules change
  useEffect(() => {
    if (permission === 'granted' && schedules.length > 0) {
      const activeReminders = checkMaintenanceReminders(schedules, dismissedIds);
      setReminders(activeReminders);
    }
  }, [schedules, dismissedIds, permission, checkMaintenanceReminders]);

  // Save dismissed IDs to localStorage
  useEffect(() => {
    localStorage.setItem('dismissed_reminders', JSON.stringify(dismissedIds));
  }, [dismissedIds]);

  const handleDismiss = (reminderId: string) => {
    setDismissedIds(prev => [...prev, reminderId]);
    if (onDismiss) onDismiss(reminderId);
  };

  const handleSnooze = (reminder: Reminder) => {
    // Snooze for 1 day by temporarily dismissing
    handleDismiss(reminder.id);
    
    // Remove from dismissed after 24 hours
    setTimeout(() => {
      setDismissedIds(prev => prev.filter(id => id !== reminder.id));
    }, 24 * 60 * 60 * 1000);
    
    if (onSnooze) onSnooze(reminder);
  };

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      showNotification('Notifikationer aktiverade', {
        body: 'Du kommer nu få påminnelser om kommande underhåll'
      });
    }
  };

  const getCategoryIcon = (category?: string): string => {
    const icons: Record<string, string> = {
      inspection: '🔍',
      cleaning: '🧹',
      hvac: '🌡️',
      plumbing: '🚿',
      electrical: '⚡',
      safety: '🔒',
      garden: '🌳',
      elevator: '🛗',
      other: '📋'
    };
    return icons[category || 'other'] || '📋';
  };

  const getDaysLabel = (days: number): string => {
    if (days === 0) return 'Idag';
    if (days === 1) return 'Imorgon';
    return `Om ${days} dagar`;
  };

  // Don't show widget if no reminders or notifications not supported
  if (permission === 'denied') {
    return null;
  }

  // Show enable notifications prompt if not granted
  if (permission !== 'granted') {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bell size={20} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-blue-900">Aktivera påminnelser</h4>
            <p className="text-sm text-blue-700 mt-1">
              Få notifikationer 7 dagar innan planerat underhåll
            </p>
            <Button 
              size="sm" 
              className="mt-3 bg-blue-600 hover:bg-blue-700"
              onClick={handleEnableNotifications}
            >
              <Bell size={16} className="mr-2" />
              Aktivera
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (reminders.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle size={20} className="text-green-600" />
          </div>
          <div>
            <h4 className="font-medium text-green-900">Inga påminnelser</h4>
            <p className="text-sm text-green-700">Inget underhåll inom de närmaste 7 dagarna</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button 
        className="w-full p-4 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Bell size={20} className="text-amber-600" />
          </div>
          <div className="text-left">
            <h4 className="font-medium text-amber-900">
              {reminders.length} påminnelse{reminders.length !== 1 ? 'r' : ''}
            </h4>
            <p className="text-sm text-amber-700">
              Underhåll inom 7 dagar
            </p>
          </div>
        </div>
        <div className={`transform transition-transform ${expanded ? 'rotate-180' : ''}`}>
          <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Reminder list */}
      {expanded && (
        <div className="border-t border-amber-200">
          {reminders.map((reminder) => (
            <div 
              key={reminder.id}
              className="p-4 border-b border-amber-100 last:border-b-0 bg-white/50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="text-xl">{getCategoryIcon(reminder.category)}</span>
                  <div>
                    <h5 className="font-medium text-slate-900">{reminder.title}</h5>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock size={14} className="text-amber-600" />
                      <span className={`text-sm font-medium ${
                        reminder.daysUntil <= 1 ? 'text-red-600' : 
                        reminder.daysUntil <= 3 ? 'text-amber-600' : 
                        'text-slate-600'
                      }`}>
                        {getDaysLabel(reminder.daysUntil)}
                      </span>
                      <span className="text-sm text-slate-500">
                        ({new Date(reminder.nextDate).toLocaleDateString('sv-SE')})
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-slate-700"
                    onClick={() => handleSnooze(reminder)}
                    title="Snooza 1 dag"
                  >
                    <Clock size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-red-600"
                    onClick={() => handleDismiss(reminder.id)}
                    title="Avfärda"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReminderWidget;
